import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  Grid,
  Modal,
  Tooltip,
  Typography,
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";

import { useHistory } from "react-router";

import { useModalStyles } from "../../modals/modalStyles";
import useArrayOfDestinations from "../../../../hooks/useArrayOfDestinations";
import { CTAButton } from "../../../../components/Table/AddValueButton";
import {
  RequestableSaasDestination,
  SaaSDestination,
  WarehouseDestination,
} from "../../../../destinations/destinations";
import { EmptyIcon } from "../../../../components/icons/themes/logos";

export type EditDestinationsModalProps = {
  configurableDestinations: Record<
    string,
    | SaaSDestination
    | RequestableSaasDestination
    | WarehouseDestination
    | undefined
  >;
  onClose: () => void;
  onSave: (destIDsToAdd: Set<string>, destIDsToRemove: Set<string>) => void;
  selectedDestinations: string[];
  selectedEventIDs: string[];
};

function EditDestinationsModal({
  configurableDestinations,
  onClose,
  onSave,
  selectedDestinations,
  selectedEventIDs,
}: EditDestinationsModalProps) {
  const classes = useModalStyles();
  const history = useHistory();

  const allDestinations = useArrayOfDestinations();

  const enabledDestinations = useMemo(() => {
    return allDestinations
      .filter((d) => d.enabled)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDestinations]);

  const destIDsEnabledForAllEvents = useMemo(
    () => new Set(selectedDestinations),
    [selectedDestinations]
  );

  const [checkedDestinationIDs, setCheckedDestinationIDs] = useState<
    Set<string>
  >(new Set([...destIDsEnabledForAllEvents]));

  const [destIDsToRemove, setDestIDsToRemove] = useState<Set<string>>(
    new Set<string>()
  );

  const [destIDsToAdd, setDestIDsToAdd] = useState<Set<string>>(
    new Set<string>()
  );

  const handleToggleDestination = (destinationId: string) => {
    const alreadyChecked = checkedDestinationIDs.has(destinationId);

    const addDestIDToPrevSet = (prev: Set<string>) =>
      new Set([...prev, destinationId]);

    const removeDestIDFromPrevSet = (prev: Set<string>) =>
      new Set([...prev].filter((id) => id !== destinationId));

    if (alreadyChecked) {
      setDestIDsToAdd(removeDestIDFromPrevSet);
      setDestIDsToRemove(addDestIDToPrevSet);
      setCheckedDestinationIDs(removeDestIDFromPrevSet);
    } else {
      setDestIDsToAdd(addDestIDToPrevSet);
      setDestIDsToRemove(removeDestIDFromPrevSet);
      setCheckedDestinationIDs(addDestIDToPrevSet);
    }
  };

  return (
    <Modal open={true} onClose={onClose}>
      <Card className={classes.root}>
        <CardHeader
          action={<CloseIcon onClick={onClose} />}
          classes={{ action: classes.closeIconAction }}
          title={
            selectedEventIDs.length > 1
              ? `Edit Destinations for ${selectedEventIDs.length} Events`
              : "Edit Destinations for 1 Event"
          }
        />

        <Divider />

        <CardContent>
          <Grid container spacing={2}>
            {enabledDestinations.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="body1">
                  No destinations are enabled in this environment.
                </Typography>

                <CTAButton
                  text="Configure a Destination"
                  onClick={() => history.push("/destinations/apps")}
                />
              </Grid>
            ) : (
              <Grid container item xs={12}>
                {enabledDestinations.map((enabledDestination) => {
                  const checked = checkedDestinationIDs.has(
                    enabledDestination.id
                  );

                  const disabled = !enabledDestination.enabled;

                  const Logo =
                    configurableDestinations[enabledDestination.type_slug]
                      ?.logoSquare || EmptyIcon;

                  const destinationMetaData = configurableDestinations[
                    enabledDestination.type_slug
                  ] as SaaSDestination;

                  return (
                    <Grid
                      alignItems="center"
                      key={`edit-destinations-modal-${enabledDestination.id}`}
                      className="flex items-start"
                      item
                      justifyContent="space-between"
                      container
                      xs={12}
                    >
                      <Grid item>
                        <Tooltip title={enabledDestination.name} arrow>
                          <div>
                            <Logo className="mr-2" />

                            {destinationMetaData?.name}
                            {destinationMetaData?.supportsMultiConfig && (
                              <>
                                <br />
                                <small>
                                  {destinationMetaData.multiConfigLabel}:{" "}
                                  {enabledDestination.instance_name}
                                </small>
                              </>
                            )}
                          </div>
                        </Tooltip>
                      </Grid>

                      <Grid item>
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onChange={() =>
                            handleToggleDestination(enabledDestination.id)
                          }
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Grid>
        </CardContent>

        <Divider />

        <CardActions className={classes.actions}>
          <Button
            color="inherit"
            onClick={onClose}
            size="small"
            variant="contained"
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={() => {
              onSave(destIDsToAdd, destIDsToRemove);
            }}
            size="small"
            variant="contained"
          >
            Save
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
}

export default EditDestinationsModal;
